import { Dialog, DialogProps, MessageDialog, useDialogManager } from '../components/widgets/dialog';
import Stack from '../components/widgets/stack/stack';
import React, { FunctionComponent } from 'react';
import HubspotForm from 'react-hubspot-form';
import { useNavigate } from 'react-router-dom';
import config from './config';
import { backofficeUrl, T } from './helpers';
import { useSubscriptionData } from './hooks/useSubscriptionData';

type HubspotFormDialogProps = {
    email?: string;
    onFormSubmitted: any;
    indexForm?: number;
};

export const OnboardingMultiStepDialog: FunctionComponent<HubspotFormDialogProps> = ({ email, onFormSubmitted, indexForm }) => {
    const HubSpotPortalID = 25176166;
    const OnboardingStepFormIds = ['094e2bce-19a6-4bb5-8c2f-1c7c7241ae73', '698a3fc9-5898-44a9-82b9-04ddb4ae8b85'];

    return indexForm! < OnboardingStepFormIds.length ? (
        <Dialog title='Tell us a little bit about yourself' minHeight={400} showCloseButton={false}>
            <Stack $gap='16px'>
                <p>Almost there!</p>
                <HubspotForm
                    onFormReady={(form: any) => {
                        form.find('input[name=email]')
                            .val(email ?? '')
                            .change();
                    }}
                    onFormSubmitted={onFormSubmitted}
                    portalId={HubSpotPortalID}
                    formId={OnboardingStepFormIds[indexForm!]}
                ></HubspotForm>
            </Stack>
        </Dialog>
    ) : null;
};

export const SellerDataFormDialog: FunctionComponent<HubspotFormDialogProps> = ({ onFormSubmitted }) => {
    const HubSpotPortalID = 25176166;
    const FormID = 'b1504a90-93eb-4dce-81a7-944af960ef8c';

    return (
        <Dialog title={T._('Help us support you better', 'Admin')} minHeight={400} showCloseButton={false}>
            <Stack $gap='16px'>
                <p>{T._('Just a quick question to ensure we\u2019re meeting your needs.', 'Admin')}</p>
                <p>
                    {T._(
                        'We want to provide support that fits your needs perfectly. Share a little more with us, and we\u2019ll tailor our support to serve you better.',
                        'Admin'
                    )}
                </p>
                <HubspotForm onFormSubmitted={onFormSubmitted} portalId={HubSpotPortalID} formId={FormID}></HubspotForm>
            </Stack>
        </Dialog>
    );
};

type NoIntegrationDialogProps = {
    message?: React.ReactNode;
    titleNoIntegration?: string;
    subTitleNoIntegration?: string;
    productId?: number;
    redirectPage?: string;
} & DialogProps;

export const NoIntegrationDialog: FunctionComponent<NoIntegrationDialogProps> = ({
    message,
    titleNoIntegration,
    subTitleNoIntegration,
    productId,
    redirectPage,
    ...props
}) => {
    const navigate = useNavigate();
    const { closeDialog, currentDialogId } = useDialogManager();
    return (
        <MessageDialog
            {...props}
            title={titleNoIntegration ? titleNoIntegration : T._('Connect Zakeke to your store', 'Admin')}
            message={
                <Stack $gap='12px'>
                    <p>{subTitleNoIntegration ? subTitleNoIntegration : T._('You need to connect a store', 'Admin')}</p>
                    {message ? message : <label>{T._('You need to integrate Zakeke with your store in order to publish a product live', 'Admin')}</label>}
                </Stack>
            }
            buttons={[
                {
                    label: T._('Connect your store now', 'Admin'),
                    onClick: () => {
                        if (currentDialogId) closeDialog(currentDialogId);

                        if (productId) {
                            localStorage.setItem(
                                'pendingAction',
                                JSON.stringify({
                                    type: 'publish-product',
                                    productId: productId,
                                    redirectPage: redirectPage,
                                    ts: Date.now()
                                })
                            );
                        }

                        navigate(backofficeUrl('/connect-shop'));
                    }
                }
            ]}
        />
    );
};

export const useUpgradeDialogs = () => {
    const { showDialog, closeDialog } = useDialogManager();
    const { sellerMarketplace } = useSubscriptionData();

    const showPlanUpgradeDialog = (message: string) => {
        showDialog(
            'plan-upgrade',
            <MessageDialog
                message={
                    sellerMarketplace === 'MICROSOFT_AZURE' ? (
                        <React.Fragment>
                            {message}
                            <br />
                            {T._('Go to your Zakeke app Saas from Azure portal to upgrade the plan.', 'Admin')}
                        </React.Fragment>
                    ) : config.isOnCustomplan ? (
                        T._('It seems you have a custom plan, please contact us at support@zakeke.zendesk.com to ask for changes to your plan', 'Admin')
                    ) : (
                        message
                    )
                }
                buttons={
                    config.isOnCustomplan || sellerMarketplace === 'MICROSOFT_AZURE'
                        ? [
                              {
                                  label: T._('OK', 'Admin'),
                                  outline: false,
                                  onClick: () => closeDialog('plan-upgrade')
                              }
                          ]
                        : [
                              {
                                  label: T._('Cancel', 'Admin'),
                                  outline: true,
                                  onClick: () => closeDialog('plan-upgrade')
                              },
                              {
                                  label: T._('Upgrade plan', 'Admin'),
                                  onClick: () => {
                                      window.open('/' + config.culture + '/Admin/Seller/Subscription', '_blank');
                                      closeDialog('plan-upgrade');
                                  }
                              }
                          ]
                }
            />
        );
    };

    const showAddonUpgradeDialog = (message: string) => {
        showDialog(
            'addon-upgrade',
            <MessageDialog
                message={message}
                buttons={[
                    {
                        label: T._('Cancel', 'Admin'),
                        outline: true,
                        onClick: () => closeDialog('addon-upgrade')
                    },
                    {
                        label: T._('Go to addons page', 'Admin'),
                        onClick: () => {
                            window.open('/' + config.culture + '/Admin/Seller/Addons', '_blank');
                            closeDialog('addon-upgrade');
                        }
                    }
                ]}
            />
        );
    };

    return { showPlanUpgradeDialog, showAddonUpgradeDialog };
};
